/**
 * @file AnalyticsPage.tsx
 * @brief Super Admin → Analytics dashboard.
 *
 * @details
 * W22 (25-10-2026): the business numbers from B25's read-only
 * /api/admin/analytics/business/* views, shown exactly as the API returns
 * them (investors see these):
 * - period picker (7 / 30 / 90 days, a month, custom) and a GBP / USD switch;
 * - the FX rate and its source on screen, always (one rate per response);
 * - six headline cards: MRR, ARR, active paid accounts, Free users,
 *   conversion, gross margin %;
 * - tabs (URL hash): Revenue & margin · Costs by vendor · Users & plans ·
 *   Subscribers by price · Activity (the old sessions / languages / growth
 *   panels; the old £0-cost revenue tiles are gone).
 * A null from the API is "–" with its reason, never 0. Company-admin
 * analytics are untouched and stay margin-free.
 *
 * Layout uses GridBlock section cells (hairline dividers) flush to the
 * content rail.
 *
 * @references BACKEND/src/features/admin/routes.js (/analytics/business/:view),
 *   docs/product/super-admin-analytics.md (B25 contract)
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — section grid layout
 * @updated 25-10-2026 — W22: B25 business analytics, period + currency, tabs
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Activity, Coins, CreditCard, LineChart, Users } from "lucide-react";
import { GridBand, GridBlock, GridCell } from "./components/GridBlock";
import { gridPageBleedClass } from "./components/gridPageBleed";
import { SectionTabRow, type SectionTabItem } from "@/features/billing/components/SectionTabRow";
import { ActivityTab } from "./ActivityTab";
import {
  useBusinessCosts,
  useBusinessMargin,
  useBusinessOverview,
  useBusinessSaas,
  useSubscribersByPrice,
} from "@/features/business-analytics/api";
import {
  fxLabel,
  resolveAnalyticsTab,
  windowFor,
  windowLabel,
  type Period,
} from "@/features/business-analytics/businessMath";
import type { Currency, FxQuote } from "@/features/business-analytics/types";
import { PeriodBar } from "@/features/business-analytics/components/PeriodBar";
import { OverviewCards } from "@/features/business-analytics/components/OverviewCards";
import { ErrorNote } from "@/features/business-analytics/components/common";
import { RevenueMarginTab } from "@/features/business-analytics/tabs/RevenueMarginTab";
import { CostsTab } from "@/features/business-analytics/tabs/CostsTab";
import { UsersPlansTab } from "@/features/business-analytics/tabs/UsersPlansTab";
import { SubscribersTab } from "@/features/business-analytics/tabs/SubscribersTab";

const CURRENCY_KEY = "vz.analytics.currency";

function readCurrency(): Currency {
  try {
    return localStorage.getItem(CURRENCY_KEY) === "usd" ? "usd" : "gbp";
  } catch {
    return "gbp";
  }
}

const TABS: SectionTabItem[] = [
  { id: "revenue", label: "Revenue & margin", icon: LineChart, href: "#revenue" },
  { id: "costs", label: "Costs by vendor", icon: Coins, href: "#costs" },
  { id: "users", label: "Users & plans", icon: Users, href: "#users" },
  { id: "subscribers", label: "Subscribers by price", icon: CreditCard, href: "#subscribers" },
  { id: "activity", label: "Activity", icon: Activity, href: "#activity" },
];

export function AnalyticsPage() {
  const [period, setPeriod] = useState<Period>({ kind: "30d" });
  const [currency, setCurrency] = useState<Currency>(readCurrency);
  useEffect(() => {
    try {
      localStorage.setItem(CURRENCY_KEY, currency);
    } catch {
      /* private window: the switch still works for this visit */
    }
  }, [currency]);

  const win = useMemo(() => windowFor(period), [period]);
  const tab = resolveAnalyticsTab(useLocation().hash);

  const overview = useBusinessOverview(win);
  const margin = useBusinessMargin(win, tab === "revenue");
  const costs = useBusinessCosts(win, tab === "costs");
  const saas = useBusinessSaas(win, tab === "users");
  const subs = useSubscribersByPrice(tab === "subscribers");

  // Every payload states its own quote; show each distinct one in view.
  const fxLines = useMemo(() => {
    const quotes: (FxQuote | undefined)[] = [
      overview.data?.fx,
      tab === "revenue" ? margin.data?.fx : tab === "costs" ? costs.data?.fx : tab === "users" ? saas.data?.fx : tab === "subscribers" ? subs.data?.fx : undefined,
    ];
    return [...new Set(quotes.filter(Boolean).map((q) => fxLabel(q)))];
  }, [overview.data, margin.data, costs.data, saas.data, subs.data, tab]);

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand>
          <h2 className="text-xl font-semibold tracking-tight">Analytics</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Revenue, real vendor cost and gross margin per product, MRR and ARR, plans, conversion and churn. Money is
            as charged (VAT included) and before Stripe fees.
          </p>
        </GridBand>

        <PeriodBar period={period} onPeriod={setPeriod} currency={currency} onCurrency={setCurrency} />

        <GridBand className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-muted/25 py-2.5 text-xs text-muted-foreground">
          {win ? (
            <span>
              <span className="font-medium text-foreground">{windowLabel(win)}</span> · by {win.groupBy}
            </span>
          ) : (
            <span className="font-medium text-foreground">Pick a start and an end day.</span>
          )}
          {fxLines.map((l) => (
            <span key={l} data-fx-line>
              {l}
            </span>
          ))}
          {currency === "usd" ? <span>Revenue in £ is converted to $ at the same rate.</span> : null}
        </GridBand>

        {win ? (
          <>
            {overview.error ? (
              <GridCell padded={false}>
                <ErrorNote error={overview.error} what="the headline numbers" />
              </GridCell>
            ) : (
              <OverviewCards data={overview.data} isLoading={overview.isLoading} currency={currency} />
            )}

            <SectionTabRow tabs={TABS} activeId={tab} ariaLabel="Analytics sections" gridClassName="" scroll />

            <div role="tabpanel" aria-label={TABS.find((t) => t.id === tab)?.label}>
              {tab === "revenue" ? (
                <RevenueMarginTab data={margin.data} isLoading={margin.isLoading} error={margin.error} currency={currency} />
              ) : tab === "costs" ? (
                <CostsTab data={costs.data} isLoading={costs.isLoading} error={costs.error} currency={currency} />
              ) : tab === "users" ? (
                <UsersPlansTab data={saas.data} isLoading={saas.isLoading} error={saas.error} currency={currency} />
              ) : tab === "subscribers" ? (
                <SubscribersTab data={subs.data} isLoading={subs.isLoading} error={subs.error} />
              ) : (
                <ActivityTab win={win} />
              )}
            </div>
          </>
        ) : null}
      </GridBlock>
    </div>
  );
}
